import React, { useCallback, useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { Line } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';
import type { KpiDrilldownSpec } from '../kpiDashboardApi';

const LINE_BLUE = '#2F5597';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface KpiProblemsMonthlyLineChartProps {
  monthlyTrendProblem: number[];
  /** Same length as `monthlyTrendProblem` when provided (KPI charts API). */
  monthLabels?: string[];
  onDrill?: (spec: KpiDrilldownSpec) => void;
}

const KpiProblemsMonthlyLineChart: React.FC<KpiProblemsMonthlyLineChartProps> = ({
  monthlyTrendProblem,
  monthLabels,
  onDrill,
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const n = monthlyTrendProblem.length;
  const labels = useMemo(() => {
    if (monthLabels && monthLabels.length === n) return monthLabels;
    if (n === 12) return MONTH_LABELS;
    return Array.from({ length: n }, (_, i) => String(i + 1));
  }, [monthLabels, n]);
  const maxVol = Math.max(0, ...monthlyTrendProblem, 1);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = chartRef.current;
      if (!chart || !onDrill) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const v = monthlyTrendProblem[el.index];
      if (v == null || v <= 0) return;
      onDrill({ kind: 'trend', monthIndex: el.index, series: 'Problem' });
    },
    [onDrill, monthlyTrendProblem]
  );

  return (
    <KpiChartPanel title="Problems by Month" className="h-full w-full" flexFill>
      <div className="relative min-h-[260px] w-full flex-1 px-1">
        <Line
          ref={chartRef}
          onClick={onDrill ? handleClick : undefined}
          data={{
            labels,
            datasets: [
              {
                label: 'Problems',
                data: monthlyTrendProblem,
                borderColor: LINE_BLUE,
                backgroundColor: 'rgba(47, 85, 151, 0.12)',
                fill: true,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 7,
                pointBackgroundColor: '#fff',
                pointBorderColor: LINE_BLUE,
                pointBorderWidth: 2,
                borderWidth: 2,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  clip: false,
                  color: LINE_BLUE,
                  font: { weight: 600, size: 10 },
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 16, right: 8, bottom: 4 } },
            interaction: { mode: 'nearest', intersect: true },
            onHover: (event, elements, chart) => {
              const canvas = chart.canvas;
              if (!elements.length) {
                canvas.style.cursor = 'default';
                return;
              }
              const idx = elements[0].index;
              const v = monthlyTrendProblem[idx];
              canvas.style.cursor = onDrill && v != null && v > 0 ? 'pointer' : 'default';
            },
            plugins: {
              legend: { display: false },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#64748b', maxRotation: 0 },
              },
              y: {
                beginAtZero: true,
                suggestedMax: maxVol * 1.12,
                grid: { color: 'rgba(148, 163, 184, 0.35)' },
                border: { display: false },
                ticks: {
                  color: '#94a3b8',
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiProblemsMonthlyLineChart;
